import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Tooltip,
  Box,
  Typography,
  CircularProgress,
  Alert,
  TablePagination,
  Collapse,
  Badge,
  Button,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Download as DownloadIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  BrokenImage as NoImageIcon,
  Link as LinkIcon,
  Scanner as ScannerIcon,
  Description as DocumentIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Collections as CollectionsIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { RxPrescription, rxService } from '../../../services/rxService';
import { RxStatusBadge } from './RxStatusBadge';
import { RxAdjStateBadge } from '../../common/RxStatusBadge';

interface RxTableGroupedProps {
  prescriptions: RxPrescription[];
  loading: boolean;
  error: string | null;
  onViewImage: (prescription: RxPrescription) => void;
  onDownloadImage: (prescription: RxPrescription) => void;
  onViewAllImages?: (prescriptions: RxPrescription[]) => void;
  filter?: 'active' | 'inactive' | 'all';
  onFilterChange?: (filter: 'active' | 'inactive' | 'all') => void;
}

interface GroupedRx {
  rxNumber: string;
  drugName: string;
  drugStrength: string;
  drugDIN: string;
  drugForm: string;
  latestFillDate: string | null;
  latestScanDate: string | null;
  images: RxPrescription[];
  // Status from the latest prescription
  status?: number;
  statusDescription?: string;
  statusCategory?: string;
  canFill?: boolean;
  inactive?: boolean;
  isMistake?: boolean;
  rxAdjState?: number | null;
}

export const RxTableGrouped: React.FC<RxTableGroupedProps> = ({
  prescriptions,
  loading,
  error,
  onViewImage,
  onDownloadImage,
  onViewAllImages,
  filter = 'active',
  onFilterChange,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Group prescriptions by RX number
  const groupedPrescriptions = useMemo(() => {
    const groups: { [key: string]: GroupedRx } = {};

    prescriptions.forEach((rx) => {
      const groupKey = rx.rxNumber || rx.referenceNumber || `unlinked_${rx.id}`;

      if (!groups[groupKey]) {
        groups[groupKey] = {
          rxNumber: rx.rxNumber || rx.referenceNumber || 'Unlinked',
          drugName: rx.drugName,
          drugStrength: rx.drugStrength,
          drugDIN: rx.drugDIN,
          drugForm: rx.drugForm,
          latestFillDate: rx.fillDate,
          latestScanDate: rx.scanDate,
          images: [],
          // Initialize status from first prescription
          status: rx.status,
          statusDescription: rx.statusDescription,
          statusCategory: rx.statusCategory,
          canFill: rx.canFill,
          inactive: rx.inactive,
          isMistake: rx.isMistake,
          rxAdjState: rx.rxAdjState,
        };
      }

      groups[groupKey].images.push(rx);

      // Update latest dates and status
      if (rx.fillDate) {
        const currentLatestFill = groups[groupKey].latestFillDate;
        if (!currentLatestFill || new Date(rx.fillDate) > new Date(currentLatestFill)) {
          groups[groupKey].latestFillDate = rx.fillDate;
        }
      }
      if (rx.scanDate) {
        const currentLatestScan = groups[groupKey].latestScanDate;
        if (!currentLatestScan || new Date(rx.scanDate) > new Date(currentLatestScan)) {
          groups[groupKey].latestScanDate = rx.scanDate;
          // Update status to the latest scanned prescription's status
          groups[groupKey].status = rx.status;
          groups[groupKey].statusDescription = rx.statusDescription;
          groups[groupKey].statusCategory = rx.statusCategory;
          groups[groupKey].canFill = rx.canFill;
          groups[groupKey].inactive = rx.inactive;
          groups[groupKey].isMistake = rx.isMistake;
        }
      }
    });

    // Sort images within each group by scan date descending
    Object.values(groups).forEach(group => {
      group.images.sort((a, b) => {
        const dateA = a.scanDate ? new Date(a.scanDate).getTime() : 0;
        const dateB = b.scanDate ? new Date(b.scanDate).getTime() : 0;
        return dateB - dateA;
      });
    });

    // Convert to array and sort by latest scan date
    return Object.values(groups).sort((a, b) => {
      const dateA = a.latestScanDate ? new Date(a.latestScanDate).getTime() : 0;
      const dateB = b.latestScanDate ? new Date(b.latestScanDate).getTime() : 0;
      return dateB - dateA;
    });
  }, [prescriptions]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const toggleRowExpansion = (rxNumber: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(rxNumber)) {
      newExpanded.delete(rxNumber);
    } else {
      newExpanded.add(rxNumber);
    }
    setExpandedRows(newExpanded);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MM/dd/yyyy');
    } catch {
      return dateString;
    }
  };

  const getImageIcon = (mimeType: string) => {
    if (!mimeType) return <NoImageIcon />;
    if (mimeType.includes('pdf')) return <PdfIcon />;
    return <ImageIcon />;
  };

  const getImageTypeChip = (mimeType: string) => {
    if (!mimeType) return null;

    let label = 'Unknown';
    let color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' = 'default';

    if (mimeType.includes('jpeg') || mimeType.includes('jpg')) {
      label = 'JPEG';
      color = 'primary';
    } else if (mimeType.includes('png')) {
      label = 'PNG';
      color = 'info';
    } else if (mimeType.includes('tiff')) {
      label = 'TIFF';
      color = 'secondary';
    } else if (mimeType.includes('pdf')) {
      label = 'PDF';
      color = 'error';
    }

    return <Chip label={label} size="small" color={color} />;
  };

  const getImageTypeSummary = (images: RxPrescription[]) => {
    const scriptImages = images.filter(img => img.scriptImageType === 1).length;
    const hardCopies = images.filter(img => img.scriptImageType === 2).length;
    const unknown = images.filter(img => !img.scriptImageType || (img.scriptImageType !== 1 && img.scriptImageType !== 2)).length;

    const parts = [];
    if (scriptImages > 0) parts.push(`${scriptImages} Script`);
    if (hardCopies > 0) parts.push(`${hardCopies} Hard Copy`);
    if (unknown > 0) parts.push(`${unknown} Unknown`);

    return parts.join(', ');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (groupedPrescriptions.length === 0) {
    return (
      <Box p={4} textAlign="center">
        <Typography variant="h6" color="textSecondary">
          No prescriptions found for this patient
        </Typography>
      </Box>
    );
  }

  const paginatedGroups = groupedPrescriptions.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Paper elevation={0}>
      {/* Filter Toggle */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={(_, value) => {
            if (value && onFilterChange) {
              onFilterChange(value);
            }
          }}
          aria-label="prescription filter"
          size="small"
        >
          <ToggleButton value="active" aria-label="active prescriptions">
            Active
          </ToggleButton>
          <ToggleButton value="inactive" aria-label="inactive prescriptions">
            Inactive
          </ToggleButton>
          <ToggleButton value="all" aria-label="all prescriptions">
            All
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width="40"></TableCell>
              <TableCell>Rx Number</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Drug Name</TableCell>
              <TableCell>Strength</TableCell>
              <TableCell>DIN</TableCell>
              <TableCell>Images</TableCell>
              <TableCell>Latest Fill</TableCell>
              <TableCell>Latest Scan</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedGroups.map((group) => {
              const isExpanded = expandedRows.has(group.rxNumber);
              const hasMultipleImages = group.images.length > 1;

              return (
                <React.Fragment key={group.rxNumber}>
                  <TableRow hover>
                    <TableCell>
                      {hasMultipleImages && (
                        <IconButton
                          size="small"
                          onClick={() => toggleRowExpansion(group.rxNumber)}
                        >
                          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        {group.rxNumber && group.rxNumber !== 'Unlinked' ? (
                          <Tooltip title="Prescription linked to Rx number">
                            <LinkIcon fontSize="small" color="primary" />
                          </Tooltip>
                        ) : (
                          <Tooltip title="Scanned document - no Rx number linked">
                            <ScannerIcon fontSize="small" color="disabled" />
                          </Tooltip>
                        )}
                        <Typography variant="body2" fontWeight="medium">
                          {group.rxNumber}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <RxStatusBadge
                        status={group.status}
                        statusDescription={group.statusDescription}
                        statusCategory={group.statusCategory}
                        canFill={group.canFill}
                        inactive={group.inactive}
                        isMistake={group.isMistake}
                        size="small"
                        showFillable={true}
                      />
                      <RxAdjStateBadge adjState={group.rxAdjState} size="small" />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {group.drugName || 'Unknown Drug'}
                        </Typography>
                        {group.drugForm && (
                          <Typography variant="caption" color="textSecondary">
                            {group.drugForm}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{group.drugStrength || 'N/A'}</TableCell>
                    <TableCell>{group.drugDIN || 'N/A'}</TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Badge badgeContent={group.images.length} color="primary">
                          <CollectionsIcon fontSize="small" />
                        </Badge>
                        <Typography variant="caption" color="textSecondary">
                          {getImageTypeSummary(group.images)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{formatDate(group.latestFillDate)}</TableCell>
                    <TableCell>{formatDate(group.latestScanDate)}</TableCell>
                    <TableCell align="center">
                      {!hasMultipleImages && group.images[0] && (
                        <Box display="flex" gap={1} justifyContent="center">
                          <Tooltip title={group.images[0].hasImage ? "View Image" : "No image available"}>
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => onViewImage(group.images[0])}
                                disabled={!group.images[0].hasImage}
                              >
                                <ViewIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={group.images[0].hasImage ? "Download Image" : "No image available"}>
                            <span>
                              <IconButton
                                size="small"
                                color="secondary"
                                onClick={() => onDownloadImage(group.images[0])}
                                disabled={!group.images[0].hasImage}
                              >
                                <DownloadIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      )}
                      {hasMultipleImages && onViewAllImages && (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<CollectionsIcon />}
                          onClick={() => onViewAllImages(group.images)}
                        >
                          View All ({group.images.length})
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>

                  {hasMultipleImages && (
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={10}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box margin={1}>
                            <Typography variant="subtitle2" gutterBottom>
                              Image Details
                            </Typography>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Type</TableCell>
                                  <TableCell>Scan Date</TableCell>
                                  <TableCell>Format</TableCell>
                                  <TableCell>Pages</TableCell>
                                  <TableCell>Description</TableCell>
                                  <TableCell align="center">Actions</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {group.images.map((image, idx) => (
                                  <TableRow key={`${image.id}_${idx}`}>
                                    <TableCell>
                                      <Chip
                                        label={rxService.getImageTypeLabel(image.scriptImageType)}
                                        size="small"
                                        color={image.scriptImageType === 1 ? 'primary' : image.scriptImageType === 2 ? 'secondary' : 'default'}
                                        variant={image.scriptImageType === 1 ? 'outlined' : 'filled'}
                                      />
                                    </TableCell>
                                    <TableCell>{formatDate(image.scanDate)}</TableCell>
                                    <TableCell>
                                      {image.hasImage ? getImageTypeChip(image.contentType) : <Chip label="No Image" size="small" />}
                                    </TableCell>
                                    <TableCell>{image.pageCount || 1}</TableCell>
                                    <TableCell>{image.description || '-'}</TableCell>
                                    <TableCell align="center">
                                      <Box display="flex" gap={1} justifyContent="center">
                                        <Tooltip title={image.hasImage ? "View Image" : "No image available"}>
                                          <span>
                                            <IconButton
                                              size="small"
                                              color="primary"
                                              onClick={() => onViewImage(image)}
                                              disabled={!image.hasImage}
                                            >
                                              <ViewIcon />
                                            </IconButton>
                                          </span>
                                        </Tooltip>
                                        <Tooltip title={image.hasImage ? "Download Image" : "No image available"}>
                                          <span>
                                            <IconButton
                                              size="small"
                                              color="secondary"
                                              onClick={() => onDownloadImage(image)}
                                              disabled={!image.hasImage}
                                            >
                                              <DownloadIcon />
                                            </IconButton>
                                          </span>
                                        </Tooltip>
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={groupedPrescriptions.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};